import NiceModal from "@ebay/nice-modal-react";
import { Col, Row, Space, TableColumnsType, Tag, Typography } from "antd";
import dayjs from "dayjs";
import * as React from "react";
import { useRecoilValue } from "recoil";
import {
  AddButton,
  AddQuestModal,
  Button,
  QuestDashboard,
  QuestDetailModal,
  Select,
  Table,
} from "src/components";
import {
  getQuestTypeLabel,
  getRewardTypeLabel,
  QuestDetail,
  useGetAllQuests,
  useListAffiliateCampaigns,
} from "src/hooks/affiliate";
import { authInfoState } from "src/recoils";
import { Scope } from "src/types";

const { Title } = Typography;

const formatDateTime = (value?: string) =>
  value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "-";

const AffiliateCampaignQuestCreate: React.FunctionComponent = () => {
  const { scope } = useRecoilValue(authInfoState);
  const canCreateQuest = scope[Scope.AffiliateQuestCreateButton]?.enable;
  const { data, isFetching, refetch } = useGetAllQuests();
  const [showAll, setShowAll] = React.useState(false);
  const [campaignFilter, setCampaignFilter] = React.useState<string | undefined>(undefined);

  const { data: campaignList, isLoading: isCampaignsLoading } = useListAffiliateCampaigns();
  const campaignOptions = React.useMemo(
    () =>
      (campaignList?.campaigns || []).map((c) => ({
        value: c.campaignCode,
        label: `${c.campaignCode} — ${c.campaignName}`,
      })),
    [campaignList],
  );

  // ค่าเริ่มต้น: แสดงเฉพาะ quest ที่ยังไม่หมดอายุ (end_date >= ตอนนี้). ปุ่ม "แสดงทั้งหมด" เพื่อดูที่หมดอายุด้วย
  // กรองเพิ่มตาม campaign ที่เลือกจาก dropdown (ถ้ามี)
  const visibleQuests = React.useMemo(() => {
    let result = data?.quests || [];
    if (!showAll) {
      const now = dayjs();
      result = result.filter((q) => !q.endDate || dayjs(q.endDate).isAfter(now));
    }
    if (campaignFilter) {
      result = result.filter((q) => q.campaignCode === campaignFilter);
    }
    return result;
  }, [data, showAll, campaignFilter]);

  const openAddQuestModal = () => {
    NiceModal.show(AddQuestModal, {
      onSuccess: () => refetch(),
    });
  };

  const openCopyQuestModal = (quest: QuestDetail) => {
    NiceModal.show(AddQuestModal, {
      prefillQuest: quest,
      onSuccess: () => refetch(),
    });
  };

  const openDetailModal = (quest: QuestDetail) => {
    NiceModal.show(QuestDetailModal, { quest });
  };

  const columns: TableColumnsType<QuestDetail> = [
    {
      title: "รหัสแคมเปญ",
      dataIndex: "campaignCode",
      key: "campaignCode",
      width: 140,
      sorter: (a, b) => (a.campaignCode || "").localeCompare(b.campaignCode || ""),
      render: (v?: string | null) => v ?? "-",
    },
    {
      title: "ชื่อแคมเปญ",
      dataIndex: "campaignName",
      key: "campaignName",
      width: 180,
      sorter: (a, b) => (a.campaignName || "").localeCompare(b.campaignName || ""),
      render: (v?: string | null) => v ?? "-",
    },
    {
      title: "ชื่อเควส",
      dataIndex: "questName",
      key: "questName",
      width: 220,
      sorter: (a, b) => a.questName.localeCompare(b.questName),
    },
    {
      title: "ประเภทเควส",
      dataIndex: "questType",
      key: "questType",
      width: 280,
      render: (v: string) => <Tag>{getQuestTypeLabel(v)}</Tag>,
      filters: Array.from(
        new Set((data?.quests || []).map((q) => q.questType)),
      ).map((s) => ({ text: getQuestTypeLabel(s), value: s })),
      onFilter: (value, record) => record.questType === value,
    },
    {
      title: "เป้าหมาย",
      dataIndex: "targetValue",
      key: "targetValue",
      width: 90,
      align: "right",
    },
    {
      title: "รางวัล",
      key: "reward",
      width: 180,
      render: (_, record) => (
        <>
          <Tag color="gold">{getRewardTypeLabel(record.rewardType)}</Tag>
          {record.rewardValue != null ? record.rewardValue.toLocaleString() : "-"}
        </>
      ),
    },
    {
      title: "ชั้น (Tier)",
      key: "tier",
      width: 80,
      align: "center",
      render: (_, record) => (record.isAllTier ? "All" : record.tier ?? "-"),
    },
    {
      title: "วันที่เริ่ม",
      dataIndex: "startDate",
      key: "startDate",
      width: 150,
      render: formatDateTime,
      sorter: (a, b) => dayjs(a.startDate).diff(dayjs(b.startDate)),
    },
    {
      title: "วันที่สิ้นสุด",
      dataIndex: "endDate",
      key: "endDate",
      width: 150,
      render: formatDateTime,
      sorter: (a, b) => dayjs(a.endDate).diff(dayjs(b.endDate)),
    },
    {
      title: "เปิดใช้งาน",
      dataIndex: "isActive",
      key: "isActive",
      width: 80,
      align: "center",
      render: (v: boolean) =>
        v ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>,
      filters: [
        { text: "Active", value: true },
        { text: "Inactive", value: false },
      ],
      onFilter: (value, record) => record.isActive === value,
    },
    {
      title: "",
      key: "action",
      width: 200,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Button size="small" onClick={() => openDetailModal(record)}>
            ดูเพิ่มเติม
          </Button>
          <Button size="small" type="primary" onClick={() => openCopyQuestModal(record)}>
            คัดลอก
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <QuestDashboard quests={data?.quests || []} />

      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            Affiliate Campaign Quest ({visibleQuests.length})
          </Title>
        </Col>
        <Col>
          <Space>
            <Select
              width="260px"
              allowClear
              showSearch
              loading={isCampaignsLoading}
              placeholder="กรองตาม Campaign"
              value={campaignFilter}
              onChange={(v) => setCampaignFilter(v as string | undefined)}
              options={campaignOptions}
              optionFilterProp="label"
              notFoundContent={isCampaignsLoading ? "กำลังโหลด..." : "ไม่พบ campaign"}
            />
            <Button onClick={() => setShowAll((prev) => !prev)}>
              {showAll ? "แสดงเฉพาะที่ยังไม่หมดอายุ" : "แสดงทั้งหมด"}
            </Button>
            {canCreateQuest && (
              <AddButton onClick={openAddQuestModal}>เพิ่ม Quest</AddButton>
            )}
          </Space>
        </Col>
      </Row>

      <Table
        rowKey="uid"
        loading={isFetching}
        columns={columns}
        dataSource={visibleQuests}
        pagination={{ pageSize: 10, showSizeChanger: true }}
        scroll={{ x: 1750 }}
      />
    </div>
  );
};

export default AffiliateCampaignQuestCreate;
